import { cn } from "@/lib/utils";
import { useCurrentTenant } from "@/hooks/useTenant";

interface BrandProps {
  /** "lockup" = symbol + wordmark, "symbol" = the W-compass alone. */
  variant?: "lockup" | "symbol";
  /** "sm" = header (32px art), "lg" = sign-in (56px art). */
  size?: "sm" | "lg";
  /** Force the reversed artwork — for grounds that are midnight in both
   *  appearances (the dock, the sign-in brand panel). */
  onDark?: boolean;
  className?: string;
  /** Kept for older call sites — same as variant="symbol". */
  collapsed?: boolean;
}

/**
 * The Wachsen Markets logo.
 *
 * The art is an all-gradient blue→green mark with no black or white in it, so
 * one file reads on both the light page and midnight; `onDark` is kept for
 * call sites but needs no separate artwork. A broker-uploaded logo (tenant
 * branding) wins.
 */
const V = "?v=2";
export function Brand({ variant = "lockup", size = "sm", className, collapsed }: BrandProps) {
  const { data } = useCurrentTenant();
  const tenant = data?.tenant;
  const isBroker = tenant && !tenant.isPlatform;
  const displayName = isBroker ? tenant.name : "Wachsen Markets";
  const branding = isBroker ? tenant.settings?.branding : undefined;
  const uploaded = branding?.logoLightUrl ?? (isBroker ? tenant.logoUrl : null);

  if (collapsed || variant === "symbol") {
    return (
      <img
        src={`/brand/wachsen-symbol.png${V}`}
        alt={`${displayName} symbol`}
        className={cn(size === "lg" ? "h-14 w-14" : "h-9 w-9", "object-contain", className)}
        draggable={false}
      />
    );
  }

  const art = size === "lg" ? "h-[72px] w-auto" : "h-11 w-auto";

  if (uploaded) {
    return <img src={uploaded} alt={`${displayName} logo`} className={cn(art, "object-contain", className)} />;
  }

  return (
    <img src={`/brand/wachsen-logo.png${V}`} alt={`${displayName} logo`} className={cn(art, "select-none object-contain", className)} draggable={false} />
  );
}
